import {
  EXAM_TYPE_CATEGORY,
  EXAM_TYPE_CATEGORY_LABELS,
  type ExamTypeCategory,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import {
  type ExamType,
  toExamTypes,
  toGqlExamType,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type { WorksheetSet } from "$global-providers/rxdb-provider/collections/epocus-program";
import {
  Box,
  Checkbox,
  FormControlLabel,
  styled,
  Typography,
} from "@material-ui/core";
import { useMemo } from "react";
import { EXAM_LABELS } from "../../../_lib/epocus-exam-constants";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import {
  selectEnabledExamTypes,
  worksheetSetExamTypes,
} from "../../../_lib/use-enabled-exam-types";
import { SettingsCard, SettingsCardLabel } from "../../_lib/settings-card";
import type { EpocusProgramConfigQuery } from "./__generated__/program-config-operations.gql";
import { useEpocusSetProgramEnabledExamTypesMutation } from "./__generated__/program-config-operations.gql";
import { SelectionSaveRow } from "./selection-save-row";
import { useProgramSelection } from "./use-program-selection";

const OptionList = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const OptionRow = styled(FormControlLabel)({
  margin: 0,
  minHeight: 44,
});

const GroupLabel = styled(Typography)({
  fontSize: rem(13),
  fontWeight: 700,
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 8,
});

const groupByCategory = (
  examTypes: readonly ExamType[],
): [ExamTypeCategory, ExamType[]][] => {
  const groups = new Map<ExamTypeCategory, ExamType[]>();
  for (const examType of examTypes) {
    const category = EXAM_TYPE_CATEGORY[examType];
    groups.set(category, [...(groups.get(category) ?? []), examType]);
  }
  return [...groups.entries()];
};

export const ExamTypesCard: FC<{
  programId: string;
  config: EpocusProgramConfigQuery["programConfig"] | undefined;
}> = ({ programId, config }) => {
  const [setEnabledExamTypes] = useEpocusSetProgramEnabledExamTypesMutation();
  const worksheetSet = (config?.worksheetSet.toLowerCase() ??
    "standard") as WorksheetSet;
  const offered = worksheetSetExamTypes(worksheetSet);

  const configured = useMemo(
    () =>
      config === undefined
        ? null
        : selectEnabledExamTypes({
            worksheetSet,
            enabledExamTypes: toExamTypes(config?.enabledExamTypes ?? []),
          }),
    [config, worksheetSet],
  );

  const selection = useProgramSelection<ExamType>(
    programId,
    configured,
    (values) =>
      setEnabledExamTypes({
        variables: {
          input: {
            programId,
            enabledExamTypes: values.map(toGqlExamType),
          },
        },
      }),
  );

  const { selected, saving, toggle } = selection;
  if (!selected) return null;

  const renderOption = (examType: ExamType) => (
    <OptionRow
      key={examType}
      control={
        <Checkbox
          color="primary"
          checked={selected.includes(examType)}
          disabled={saving}
          onChange={() => toggle(examType)}
        />
      }
      label={
        <Typography style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}>
          {EXAM_LABELS[examType]}
        </Typography>
      }
    />
  );

  return (
    <SettingsCard>
      <SettingsCardLabel>Exam types</SettingsCardLabel>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        Only the exam types you enable appear when someone starts or documents a
        scan. Exams already recorded against a type you turn off stay viewable
        and reviewable.
      </Typography>

      {worksheetSet === "standard" ? (
        <OptionList>{offered.map(renderOption)}</OptionList>
      ) : (
        groupByCategory(offered).map(([category, examTypes]) => (
          <Box key={category}>
            <GroupLabel>{EXAM_TYPE_CATEGORY_LABELS[category]}</GroupLabel>
            <OptionList
              role="group"
              aria-label={EXAM_TYPE_CATEGORY_LABELS[category]}
            >
              {examTypes.map(renderOption)}
            </OptionList>
          </Box>
        ))
      )}

      <SelectionSaveRow
        selection={selection}
        saveLabel="Save exam types"
        emptyWarning="Enable at least one exam type. Saving none would turn every exam type in this program's worksheet set back on."
      />
    </SettingsCard>
  );
};
